import { LitElement, html, css, unsafeCSS } from 'https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js'

/**
 * Skio Plan Picker — Pluma (Palo Alto theme) custom build
 * Based on https://github.com/skio-org/skio-plan-picker
 *
 * What's custom here:
 *  - Card UI (Subscribe / Try Once) matching the Figma design
 *  - "GET STARTED" / "TRY NOW" buttons that add to cart through Palo Alto's
 *    own cart flow (theme.routes.cart_add_url → `theme:cart:refresh` opens the drawer)
 *  - "Save $X" badges injected above the theme's variant option buttons,
 *    showing subscription savings vs. the one-time price for each option
 *
 * Optional `options` keys (all have defaults):
 *  subscription_title        "Subscribe"
 *  onetime_title             "Try Once"
 *  badge_text                "Extra [discount] OFF"   ([discount] = plan % or $ if discount_format=absolute)
 *  benefits                  array or newline-separated string
 *  subscription_button_text  "Get started"
 *  onetime_button_text       "Try now"
 *  recommended_suffix        " (Recommended)"  added to the default plan's name ("" to disable)
 *  default_subscription      plan name fragment to preselect (e.g. "month")
 *  variant_badge_text        "Save [savings]"
 *  show_variant_savings      true
 *  hide_theme_atc            true   hides the theme's Add to cart + Buy it now in this product form
 *  start_onetime             false  true = start on Try Once
 *  onetime_first             false  true = Try Once card on top
 *  onetime_drawbacks         array or newline-separated string shown in the Try Once card
 *                            ([discount] = the subscription discount, e.g. "25%"; "" to hide)
 */

const DEFAULT_BENEFITS = ['Change, pause, or cancel anytime!', 'Fast & free shipping forever', 'Free surprise gifts & goodies']
const DEFAULT_DRAWBACKS = ['No [discount] off', 'Shipping fees may apply']

// Shared with the selected variant option buttons (light DOM), so both match the Subscribe card.
const ACCENT = '#F58D28'
const ACCENT_BG = '#FFEBDA'

const GLOBAL_STYLE_ID = 'skio-plan-picker-global-styles'
const GLOBAL_STYLES = `
  /* Selected variant option button = same fill + border as the selected Subscribe card.
     Color swatches are left alone. */
  .skio-picker-active variant-selects .selector-wrapper:not(.selector-wrapper--swatches) input[type="radio"]:checked + label,
  .skio-picker-active variant-selects .selector-wrapper:not(.selector-wrapper--swatches) label:has(> input[type="radio"]:checked) {
    background-color: var(--skio-variant-selected-bg, ${ACCENT_BG}) !important;
    border-color: var(--skio-variant-selected-border, ${ACCENT}) !important;
  }

  .skio-hide-theme-atc [data-add-to-cart],
  .skio-hide-theme-atc [data-buy-it-now] { display: none !important; }

  /* Room above the buttons for the badges, and bigger buttons so an 11px badge fits inside the button width.
     Applies to every button in an option row that has badges, so the row stays even. */
  fieldset.skio-has-badges { padding-top: 14px; overflow: visible; }
  fieldset.skio-has-badges [data-variant-buttons] { gap: 12px; overflow: visible; }
  fieldset.skio-has-badges label {
    display: flex !important;
    align-items: center;
    justify-content: center;
    text-align: center;
    min-width: var(--skio-variant-btn-min-width, 96px);
    min-height: var(--skio-variant-btn-min-height, 52px);
    /* a little extra on top, since the badge overlaps the top edge */
    padding: 18px 16px 14px;
  }
  label.skio-has-badge { position: relative; overflow: visible; }

  /* Try Once selected: hide the badges but keep their space, so the layout doesn't jump. */
  .skio-variant-badge { transition: opacity 0.2s ease; }
  .skio-onetime-selected .skio-variant-badge { opacity: 0; visibility: hidden; }

  .skio-variant-badge {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -55%);
    z-index: 2;
    padding: 4px 10px;
    border-radius: 999px;
    background: var(--skio-variant-badge-bg, #7A222E);
    color: var(--skio-variant-badge-text, #FFFFFF);
    /* !important so the theme's option label styles can't enlarge the badge */
    font-size: var(--skio-variant-badge-size, 11px) !important;
    font-weight: 500 !important;
    line-height: 1 !important;
    letter-spacing: 0.02em !important;
    white-space: nowrap;
    max-width: calc(100% - 6px); /* never wider than its button */
    overflow: hidden;
    text-overflow: ellipsis;
    pointer-events: none;
  }

  /* Mobile: buttons share the full row width evenly, so each one is as wide as possible. */
  @media (max-width: 749px) {
    fieldset.skio-has-badges [data-variant-buttons] {
      display: flex !important;
      flex-wrap: nowrap;
      gap: 10px;
    }
    fieldset.skio-has-badges label {
      flex: 1 1 0;
      width: auto !important;
      min-width: 0;
      margin: 0 !important;
      min-height: var(--skio-variant-btn-min-height-mobile, 56px);
      padding: 20px 8px 14px;
    }
    .skio-variant-badge {
      padding: 4px 8px;
      letter-spacing: 0 !important;
    }
  }
`

export class SkioPlanPicker extends LitElement {
  static properties = {
    key: { type: String },

    product: { type: Object },
    productHandle: { type: String },
    selectedVariant: { type: Object },

    options: { type: Object },
    moneyFormat: { type: String },

    quantity: { type: Number },
    rules: { type: Array },
    rule: { type: Object },
    cart: { type: Object },

    availableSellingPlanGroups: { state: true },
    selectedSellingPlanGroup: { type: Object },
    selectedSellingPlan: { type: Object },

    disableUrl: { type: Boolean },
    debug: { type: Boolean },

    isAdding: { state: true },
    errorMessage: { state: true },
    variantUnavailable: { state: true },
  }

  static styles = css`
    :host {
      --skio-accent: ${unsafeCSS(ACCENT)};
      --skio-accent-bg: ${unsafeCSS(ACCENT_BG)};
      --skio-badge-bg: #33A091;
      --skio-badge-text: #FFFFFF;
      --skio-cta-bg: #7A222E;
      --skio-cta-text: #FFFFFF;
      --skio-text: #141414;
      --skio-text-muted: #454545;
      --skio-border: #707070;
      --skio-idle-border: #D6D6D6;
      --skio-idle-text: #B8B8B8;
      --skio-radius: 8px;

      display: block;
      width: 100%;
      margin-bottom: 16px;
      font-family: inherit;
      color: var(--skio-text);
    }

    *, *::before, *::after { box-sizing: border-box; }

    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      margin: -1px;
      padding: 0;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      border: 0;
    }

    .skio-plan-picker {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    /* ---------- Card ---------- */
    .card {
      padding: 18px 13px;
      border: 1.5px solid var(--skio-idle-border);
      border-radius: var(--skio-radius);
      background: #FFFFFF;
      cursor: pointer;
      transition: border-color 0.2s ease, background-color 0.2s ease;
    }

    .card:hover { border-color: var(--skio-border); }

    .card:focus-visible {
      outline: 2px solid var(--skio-accent);
      outline-offset: 3px;
    }

    .card--selected,
    .card--selected:hover {
      border-color: var(--skio-accent);
      background: var(--skio-accent-bg);
      cursor: default;
    }

    .card__head {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    /* ---------- Radio ---------- */
    .radio {
      flex: 0 0 24px;
      display: grid;
      place-items: center;
      width: 24px;
      height: 24px;
      border: 2px solid var(--skio-idle-border);
      border-radius: 50%;
      background: #FFFFFF;
      transition: border-color 0.2s ease;
    }

    .radio::after {
      content: '';
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: var(--skio-accent);
      transform: scale(0);
      transition: transform 0.2s ease;
    }

    .card--selected .radio { border-color: var(--skio-text); }
    .card--selected .radio::after { transform: scale(1); }

    /* ---------- Title + badge ---------- */
    .card__title-wrap {
      display: flex;
      flex: 1 1 auto;
      flex-wrap: wrap;
      align-items: center;
      gap: 6px 12px;
      min-width: 0;
    }

    .card__title {
      font-size: 20px;
      font-weight: 700;
      line-height: 1.2;
      color: var(--skio-idle-text);
      transition: color 0.2s ease;
    }

    .card--selected .card__title { color: var(--skio-text); }

    .card__badge {
      padding: 8px 13px;
      border-radius: 999px;
      background: var(--skio-badge-bg);
      color: var(--skio-badge-text);
      font-size: 14px;
      font-weight: 500;
      line-height: 1;
      white-space: nowrap;
    }

    /* ---------- Price ---------- */
    .card__price {
      display: flex;
      align-items: baseline;
      gap: 6px;
      margin-left: auto;
      white-space: nowrap;
    }

    .card__price-now {
      font-size: 18px;
      font-weight: 700;
      color: var(--skio-idle-text);
    }

    .card__price-was {
      font-size: 16px;
      font-weight: 400;
      color: var(--skio-idle-text);
    }

    .card--selected .card__price-now { color: var(--skio-text); }
    .card--selected .card__price-was { color: var(--skio-text-muted); }

    /* ---------- Body ---------- */
    .card__body {
      display: flex;
      flex-direction: column;
      gap: 20px;
      padding: 20px 0 0 36px;
      cursor: default;
    }

    .benefits {
      display: flex;
      flex-direction: column;
      gap: 14px;
      margin: 0;
      padding: 0;
      list-style: none;
    }

    .benefits li {
      display: flex;
      align-items: center;
      gap: 14px;
      font-size: 18px;
      line-height: 1.3;
      color: var(--skio-text);
    }

    .benefits svg { flex: 0 0 24px; }

    /* Try Once "what you miss out on" list */
    .benefits--drawbacks li { color: var(--skio-text-muted); }

    .plan-select,
    .plan-static {
      width: 100%;
      min-height: 52px;
      padding: 0 44px 0 20px;
      border: 1px solid var(--skio-border);
      border-radius: var(--skio-radius);
      background-color: #FFFFFF;
      color: var(--skio-text-muted);
      font: inherit;
      font-size: 17px;
    }

    .plan-select {
      appearance: none;
      -webkit-appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23454545' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 18px center;
      background-size: 18px;
      cursor: pointer;
      text-overflow: ellipsis;
    }

    .plan-select:focus-visible {
      outline: 2px solid var(--skio-accent);
      outline-offset: 2px;
    }

    .plan-static {
      display: flex;
      align-items: center;
    }

    .cta {
      width: 100%;
      min-height: 56px;
      padding: 12px 20px;
      border: 0;
      border-radius: var(--skio-radius);
      background: var(--skio-cta-bg);
      color: var(--skio-cta-text);
      font: inherit;
      font-size: 18px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      cursor: pointer;
      transition: filter 0.15s ease;
    }

    .cta:hover:not(:disabled) { filter: brightness(1.15); }

    .cta:focus-visible {
      outline: 2px solid var(--skio-cta-bg);
      outline-offset: 3px;
    }

    .cta:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .skio-error {
      margin: 10px 0 0;
      color: #B42318;
      font-size: 14px;
    }

    @media (max-width: 480px) {
      .card { padding: 16px; }
      .card__head { align-items: flex-start; gap: 10px; }
      .radio { margin-top: 2px; }
      .card__title { font-size: 20px; }
      .card__badge { padding: 6px 12px; font-size: 13px; }
      .card__price { flex-direction: column; align-items: flex-end; gap: 2px; }
      .card__price-now { font-size: 19px; }
      .card__price-was { font-size: 15px; }
      .card__body { gap: 16px; padding-left: 0; }
      .benefits li { gap: 12px; font-size: 16px; }
      .plan-select, .plan-static { font-size: 16px; }
      .cta { font-size: 16px; }
    }

    /* ---------- Setup mode (theme editor only) ---------- */
    .setupMode {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin-bottom: 1rem;
      padding: 1rem;
      color: black;
      font-size: 14px;
      border: 4px solid;
      border-image: repeating-linear-gradient(-55deg, #000, #000 20px, #ffb101 20px, #ffb101 40px) 10;
    }

    .setupMode::after {
      content: 'Setup Mode';
      position: absolute;
      top: 0;
      right: 0;
      padding: 0.25rem 1rem;
      background: black;
      color: white;
      font-size: 13px;
    }

    .setupMode .detection {
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 10px;
    }

    .setupMode .detection small { grid-column: 1 / -1; }
  `

  constructor() {
    super()
    this.debug = this.debug || window?.Shopify?.designMode
    this.variantChanged = false
    this.rules = []
    this.rule = null
    this.quantity = 1
    this.isAdding = false
    this.errorMessage = ''
    this.variantUnavailable = false

    this._badgeFrame = null
    this._badgeObserver = null
  }

  // SECTION: Lifecycle

  async connectedCallback() {
    super.connectedCallback()
    this.log('Mounted')

    if (this.productHandle && !this.product) {
      await this.fetchProduct(this.productHandle)
    }

    this.bindFormEvents()
    this.bindCartEvents()
    this.ensureGlobalStyles()
    this.closest('product-form')?.classList.add('skio-picker-active')

    if (this.options?.hide_theme_atc !== false) {
      this.closest('product-form')?.classList.add('skio-hide-theme-atc')
    }

    this.observeVariantSelects()

    try {
      this.rules = await this.fetchRules()
    } catch (err) {
      this.rules = []
      this.error('Could not load discount rules', err)
    }
    this.getRule()
  }

  disconnectedCallback() {
    this._badgeObserver?.disconnect()
    this._badgeObserver = null
    if (this._badgeFrame) cancelAnimationFrame(this._badgeFrame)
    this._badgeFrame = null
    super.disconnectedCallback()
  }

  updated(changed) {
    if (!this.viable()) return

    if (changed.has('product') && this.product) {
      this.key = this.key ? this.key : this.product.id
    }

    if (changed.has('selectedVariant') && this.selectedVariant) {
      this.refreshSellingPlanGroups()
    }

    if (changed.has('selectedSellingPlan')) {
      this.dispatchEvent(
        new CustomEvent('skio::update-selling-plan', {
          bubbles: true,
          composed: true,
          detail: { variant: this.selectedVariant, sellingPlan: this.selectedSellingPlan, key: this.key },
        })
      )
      this.dispatchEvent(new CustomEvent('change', { detail: { value: this.selectedSellingPlan?.id } }))
    }

    this.updateForm()
    this.updateExternalPrice()
    if (!this.disableUrl) this.updateURLParams()

    if (changed.has('quantity') || changed.has('cart')) this.getRule()

    if (changed.has('selectedVariant') || changed.has('selectedSellingPlan') || changed.has('availableSellingPlanGroups') || changed.has('rule')) {
      this.scheduleBadgeRender()
    }

    this.updateExternalElements()

    // Hides the variant savings badges while Try Once is selected.
    this.closest('product-form')?.classList.toggle('skio-onetime-selected', !this.selectedSellingPlan)
  }

  refreshSellingPlanGroups() {
    const skioGroups = (this.product.selling_plan_groups || []).filter(
      group => group.app_id === 'SKIO' && !group.name.toLowerCase().includes('hidden-') && !group.name.toLowerCase().includes('dynamic box subscription')
    )

    let groups = skioGroups.filter(group =>
      group.selling_plans.some(plan => this.selectedVariant.selling_plan_allocations.some(alloc => alloc.selling_plan_id === plan.id))
    )

    if (this.options?.combine_groups) {
      groups = [
        {
          name: this.options?.combined_group_name,
          selling_plans: groups.flatMap(group => group.selling_plans),
          id: 'combined_group',
          app_id: 'SKIO',
        },
      ]
    }

    this.availableSellingPlanGroups = groups

    if (!groups.length) {
      this.selectSellingPlanGroup(null)
      return
    }

    const urlPlanId = new URL(window.location.href).searchParams.get('selling_plan')
    const defaultName = (this.options?.default_subscription || '').trim().toLowerCase()

    groups.forEach(group => {
      const plans = this.getAvailableSellingPlans(group)
      const urlPlan = plans.find(plan => plan.id == urlPlanId)
      const samePlan = plans.find(plan => plan.name === this.selectedSellingPlan?.name)
      const defaultPlan = defaultName ? plans.find(plan => plan.name.toLowerCase().includes(defaultName)) : null

      group.selected_selling_plan = urlPlan || samePlan || defaultPlan || plans[0]
      group.selected = Boolean(urlPlan || samePlan || defaultPlan)
    })

    const startOnSubscribe = !this.variantChanged && this.options?.start_onetime !== true
    if (startOnSubscribe || this.product.requires_selling_plan || urlPlanId || this.selectedSellingPlan) {
      const group = groups.find(g => g.selected) || groups[0]
      this.selectSellingPlan(group.selected_selling_plan.id)
    } else {
      this.selectSellingPlanGroup(null)
    }
  }

  // !SECTION: Lifecycle

  // SECTION: Templates

  render() {
    if (!this.viable() || this.options?.layout === 'hidden') {
      return this.debug ? this.setupMode() : ''
    }

    if (this.selectedVariant.selling_plan_allocations.length === 0 && !this.options?.show_without_subscription) {
      return this.debug ? this.setupMode() : ''
    }

    const subscriptionCards = (this.availableSellingPlanGroups || []).map(group => this.subscriptionCard(group))
    const oneTimeCard = this.product.requires_selling_plan ? '' : this.oneTimeCard()
    const cards = this.options?.onetime_first ? [oneTimeCard, ...subscriptionCards] : [...subscriptionCards, oneTimeCard]

    return html`
      ${this.debug ? this.setupMode() : ''}
      <div class="skio-plan-picker" role="radiogroup" aria-label="Purchase options">${cards}</div>
      ${this.errorMessage ? html`<p class="skio-error" role="alert">${this.errorMessage}</p>` : ''}
    `
  }

  subscriptionCard(group) {
    const selected = this.selectedSellingPlanGroup === group
    const plan = group.selected_selling_plan
    const subscriptionPrice = this.subscriptionPrice(this.selectedVariant, plan)
    const oneTimePrice = this.selectedVariant.price
    const badge = this.badgeText(plan)
    const title = group.name === 'Prepaid' ? this.options?.prepaid_title || group.name : this.options?.subscription_title || 'Subscribe'

    return html`
      <div
        class="card ${selected ? 'card--selected' : ''}"
        role="radio"
        aria-checked=${selected ? 'true' : 'false'}
        tabindex="0"
        @click=${() => this.selectSellingPlanGroup(group)}
        @keydown=${e => this.onCardKeydown(e, group)}
      >
        <div class="card__head">
          <span class="radio" aria-hidden="true"></span>
          <div class="card__title-wrap">
            <span class="card__title">${title}</span>
            ${badge ? html`<span class="card__badge">${badge}</span>` : ''}
          </div>
          <div class="card__price" aria-live="polite">
            <span class="card__price-now">${this.money(subscriptionPrice)}</span>
            ${oneTimePrice > subscriptionPrice
              ? html`<s class="card__price-was"><span class="sr-only">Regular price </span>${this.money(oneTimePrice)}</s>`
              : ''}
          </div>
        </div>

        ${selected
          ? html`
              <div class="card__body">
                ${this.benefitsTemplate()} ${this.sellingPlanDropdown(group)}
                ${this.ctaButton(this.options?.subscription_button_text || 'Get started')}
              </div>
            `
          : ''}
      </div>
    `
  }

  oneTimeCard() {
    const selected = this.selectedSellingPlanGroup == null
    const variant = this.selectedVariant
    const showCompare = this.options?.show_compare_price && variant.compare_at_price > variant.price

    return html`
      <div
        class="card ${selected ? 'card--selected' : ''}"
        role="radio"
        aria-checked=${selected ? 'true' : 'false'}
        tabindex="0"
        @click=${() => this.selectSellingPlanGroup(null)}
        @keydown=${e => this.onCardKeydown(e, null)}
      >
        <div class="card__head">
          <span class="radio" aria-hidden="true"></span>
          <div class="card__title-wrap">
            <span class="card__title">${this.options?.onetime_title || 'Try Once'}</span>
          </div>
          <div class="card__price" aria-live="polite">
            <span class="card__price-now">${this.money(variant.price)}</span>
            ${showCompare ? html`<s class="card__price-was">${this.money(variant.compare_at_price)}</s>` : ''}
          </div>
        </div>

        ${selected
          ? html`
              <div class="card__body">
                ${this.drawbacksTemplate()} ${this.ctaButton(this.options?.onetime_button_text || 'Try now')}
              </div>
            `
          : ''}
      </div>
    `
  }

  benefitsTemplate() {
    let benefits = this.options?.benefits
    if (typeof benefits === 'string') benefits = benefits.split('\n')
    if (!Array.isArray(benefits)) benefits = DEFAULT_BENEFITS
    benefits = benefits.map(text => String(text).trim()).filter(Boolean)
    if (!benefits.length) return ''

    return html`
      <ul class="benefits">
        ${benefits.map(
          text => html`
            <li>
              <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="12" fill="var(--skio-accent)"></circle>
                <path d="M7 12.5l3.2 3.2L17 9" fill="none" stroke="#FFFFFF" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
              <span>${text}</span>
            </li>
          `
        )}
      </ul>
    `
  }

  // "What you miss out on" list for the Try Once card.
  drawbacksTemplate() {
    let items = this.options?.onetime_drawbacks
    if (typeof items === 'string') items = items.split('\n')
    if (!Array.isArray(items)) items = DEFAULT_DRAWBACKS

    // [discount] = the subscription discount of the plan Subscribe would use.
    const plan = this.availableSellingPlanGroups?.[0]?.selected_selling_plan
    const discount = plan ? this.discount(plan) : null
    const hasDiscount = discount && discount.percent > 0 && Number.isFinite(discount.percent)
    const discountText = hasDiscount
      ? this.options?.discount_format === 'absolute'
        ? this.money(discount.absolute, true)
        : `${discount.percent}%`
      : ''

    items = items
      .map(text => String(text).trim())
      .filter(Boolean)
      .filter(text => hasDiscount || !text.includes('[discount]')) // drop the line if there's no discount to show
      .map(text => text.replaceAll('[discount]', discountText))

    if (!items.length) return ''

    return html`
      <ul class="benefits benefits--drawbacks">
        ${items.map(
          text => html`
            <li>
              <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="12" fill="var(--skio-text)"></circle>
                <path d="M8.5 8.5l7 7M15.5 8.5l-7 7" fill="none" stroke="#FFFFFF" stroke-width="2.4" stroke-linecap="round"></path>
              </svg>
              <span>${text}</span>
            </li>
          `
        )}
      </ul>
    `
  }

  sellingPlanDropdown(group) {
    const plans = this.getAvailableSellingPlans(group)
    if (!plans.length) return ''

    if (plans.length === 1) {
      return html`<div class="plan-static">${this.planLabel(plans[0], group)}</div>`
    }

    return html`
      <select class="plan-select" aria-label="Delivery frequency" @change=${e => this.selectSellingPlan(e.target.value)}>
        ${plans.map(
          plan => html`<option value=${plan.id} ?selected=${group.selected_selling_plan?.id === plan.id}>${this.planLabel(plan, group)}</option>`
        )}
      </select>
    `
  }

  planLabel(plan, group) {
    const suffix = this.options?.recommended_suffix ?? ' (Recommended)'
    if (!suffix || plan.name.toLowerCase().includes('recommended')) return plan.name

    const plans = this.getAvailableSellingPlans(group)
    const defaultName = (this.options?.default_subscription || '').trim().toLowerCase()
    const recommended = (defaultName && plans.find(p => p.name.toLowerCase().includes(defaultName))) || plans[0]

    return recommended?.id === plan.id ? `${plan.name}${suffix}` : plan.name
  }

  ctaButton(label) {
    const soldOut = !this.selectedVariant?.available
    const disabled = this.isAdding || this.variantUnavailable || soldOut

    let text = label
    if (this.isAdding) text = 'Adding…'
    else if (this.variantUnavailable) text = 'Unavailable'
    else if (soldOut) text = 'Sold out'

    return html`<button type="button" class="cta" ?disabled=${disabled} @click=${e => this.handleATCClick(e)}>${text}</button>`
  }

  invalidIcon() {
    return html`<svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#D22B2B"><path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>`
  }

  validIcon() {
    return html`<svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#0BDA51"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>`
  }

  setupMode() {
    return html`
      <div class="setupMode">
        Skio Plan Picker
        <small>This will not appear on the live site.</small>
        <small>Key: ${this.key}</small>
        <div class="detection">${this.product ? this.validIcon() : this.invalidIcon()} Product</div>
        <div class="detection">${this.selectedVariant ? this.validIcon() : this.invalidIcon()} Selected Variant</div>
        <div class="detection">${this.form ? this.validIcon() : this.invalidIcon()} Form</div>
        <div class="detection">${this.variantInput ? this.validIcon() : this.invalidIcon()} Variant Input</div>
        <div class="detection">
          ${this.variantChanged ? this.validIcon() : this.invalidIcon()} Variant Change Detection
          <small>Please attempt to change the selected variant.</small>
        </div>
        <small>If any of these don't pass, see the <a target="_blank" rel="noreferrer" href="https://integrate.skio.com/troubleshooting">troubleshooting guide</a>.</small>
      </div>
    `
  }

  // !SECTION: Templates

  // SECTION: Selection

  onCardKeydown(e, group) {
    if (e.target !== e.currentTarget) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      this.selectSellingPlanGroup(group)
    }
  }

  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group
    this.selectedSellingPlan = group?.selected_selling_plan
    this.errorMessage = ''
  }

  selectSellingPlan(sellingPlanId) {
    const group = (this.availableSellingPlanGroups || []).find(g => g.selling_plans.some(plan => plan.id == sellingPlanId))
    if (!group) return

    const plan = group.selling_plans.find(p => p.id == sellingPlanId)
    group.selected_selling_plan = plan
    this.selectedSellingPlanGroup = group
    this.selectedSellingPlan = plan
    this.requestUpdate()
  }

  getAvailableSellingPlans(group) {
    if (!group || !this.selectedVariant) return []
    return group.selling_plans.filter(plan => this.selectedVariant.selling_plan_allocations.some(alloc => alloc.selling_plan_id === plan.id))
  }

  // !SECTION: Selection

  // SECTION: Pricing

  // Subscription price for any variant + plan. Uses Shopify's own allocation price when present,
  // so the card price and the variant badges always agree.
  subscriptionPrice(variant, plan) {
    if (!variant) return 0
    if (!plan) return variant.price

    const allocation = (variant.selling_plan_allocations || []).find(alloc => alloc.selling_plan_id == plan.id)
    let cents = allocation ? allocation.price : this.applyPriceAdjustment(variant.price, plan.price_adjustments?.[0])

    if (this.rule) cents = cents - cents * (this.rule.discountAmount / 100)

    return Math.max(0, Math.round(cents))
  }

  applyPriceAdjustment(price, adjustment) {
    if (!adjustment) return price
    switch (adjustment.value_type) {
      case 'percentage':
        return price - (price * adjustment.value) / 100
      case 'fixed_amount':
        return price - adjustment.value
      case 'price':
        return adjustment.value
      default:
        return price
    }
  }

  // The plan a variant's savings badge is based on: the selected plan if that variant supports it,
  // otherwise the first Skio plan the variant does support.
  planForVariant(variant, preferredPlan) {
    const allocIds = new Set((variant?.selling_plan_allocations || []).map(alloc => alloc.selling_plan_id))
    if (preferredPlan && allocIds.has(preferredPlan.id)) return preferredPlan

    const groups = (this.product?.selling_plan_groups || []).filter(
      group => group.app_id === 'SKIO' && !group.name.toLowerCase().includes('hidden-') && !group.name.toLowerCase().includes('dynamic box subscription')
    )
    for (const group of groups) {
      for (const plan of group.selling_plans) {
        if (allocIds.has(plan.id)) return plan
      }
    }
    return null
  }

  savingsFor(variant, preferredPlan) {
    const plan = this.planForVariant(variant, preferredPlan)
    if (!plan) return 0
    return Math.max(0, variant.price - this.subscriptionPrice(variant, plan))
  }

  badgeText(plan) {
    if (!plan) return ''
    const discount = this.discount(plan)
    if (!discount.percent || discount.percent <= 0 || !Number.isFinite(discount.percent)) return ''

    const value = this.options?.discount_format === 'absolute' ? this.money(discount.absolute, true) : `${discount.percent}%`
    const template = this.options?.badge_text || 'Extra [discount] OFF'
    return template.replaceAll('[discount]', value)
  }

  discount(sellingPlan) {
    if (!sellingPlan) return { percent: 0, amount: 0, absolute: 0 }
    return this.getDiscountFromPriceAdjustment(sellingPlan, sellingPlan.price_adjustments[0])
  }

  getDiscountFromPriceAdjustment(sellingPlan, priceAdjustment) {
    const discount = { percent: 0, amount: 0, absolute: 0 }
    if (!priceAdjustment) return discount

    let multiplier = 1
    const group = this.product.selling_plan_groups.find(g => g.selling_plans.some(plan => plan.id == sellingPlan.id))

    if (group?.name === 'Prepaid') {
      const matches = sellingPlan.name.match(/\b\d+\s*(days|weeks|months|years)\b/gi)
      if (matches?.length) {
        const intervals = matches[0].match(/\d+/g)
        multiplier = intervals[0] / (intervals[1] || 1)
        multiplier = multiplier > 1 ? multiplier : 1
      }
    }

    const price = this.selectedVariant.price
    const compareAtPrice = this.selectedVariant.compare_at_price && this.selectedVariant.compare_at_price > price ? this.selectedVariant.compare_at_price : price

    switch (priceAdjustment.value_type) {
      case 'percentage':
        discount.percent = priceAdjustment.value
        discount.absolute = Math.round((price * priceAdjustment.value) / 100)
        discount.amount = discount.absolute
        break
      case 'fixed_amount':
        discount.percent = Math.round((priceAdjustment.value / price) * 100)
        discount.absolute = priceAdjustment.value
        discount.amount = priceAdjustment.value
        break
      case 'price':
        discount.percent = Math.round(((compareAtPrice * multiplier - priceAdjustment.value) / (compareAtPrice * multiplier)) * 100)
        discount.absolute = compareAtPrice * multiplier - priceAdjustment.value
        discount.amount = price - priceAdjustment.value
        break
    }

    return discount
  }

  // Kept for anything on the theme that still calls price() directly.
  price(sellingPlan, formatted = true) {
    const cents = this.subscriptionPrice(this.selectedVariant, sellingPlan)
    return formatted ? this.money(cents) : cents
  }

  money(cents, trimZeros = false) {
    const value = Math.round(Number(cents) || 0)
    let out

    if (typeof window.theme?.formatMoney === 'function') {
      out = window.theme.formatMoney(value, window.theme.moneyFormat || this.moneyFormat)
    } else if (this.moneyFormat) {
      out = this.moneyFormat.replace(/{{\s*\w+\s*}}/, (value / 100).toFixed(2))
    } else {
      out = new Intl.NumberFormat(window.Shopify?.locale || 'en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(value / 100)
    }

    return trimZeros ? String(out).replace(/[.,]00(?=\D*$)/, '') : out
  }

  // !SECTION: Pricing

  // SECTION: Variant option savings badges (light DOM, inside the theme's <variant-selects>)

  ensureGlobalStyles() {
    if (document.getElementById(GLOBAL_STYLE_ID)) return
    const style = document.createElement('style')
    style.id = GLOBAL_STYLE_ID
    style.textContent = GLOBAL_STYLES
    document.head.appendChild(style)
  }

  // Palo Alto swaps out <variant-selects> after every option change, so watch for that and re-badge.
  observeVariantSelects() {
    const root = this.closest('product-info')
    if (!root || this._badgeObserver) return

    this._badgeObserver = new MutationObserver(() => this.scheduleBadgeRender())
    this._badgeObserver.observe(root, { childList: true, subtree: true })
    this.scheduleBadgeRender()
  }

  scheduleBadgeRender() {
    if (this._badgeFrame) return
    this._badgeFrame = requestAnimationFrame(() => {
      this._badgeFrame = null
      this.renderVariantBadges()
    })
  }

  renderVariantBadges() {
    if (this.options?.show_variant_savings === false) return

    const root = this.closest('product-info') || document
    const variantSelects = root.querySelector('variant-selects')
    const current = this.selectedVariant
    if (!variantSelects || !current || !this.product?.variants?.length) return

    const currentOptions = current.options || [current.option1, current.option2, current.option3].filter(v => v != null)
    const preferredPlan = this.selectedSellingPlan || this.availableSellingPlanGroups?.[0]?.selected_selling_plan || null
    const template = this.options?.variant_badge_text || 'Save [savings]'

    variantSelects.querySelectorAll('fieldset').forEach((fieldset, fieldsetIndex) => {
      const position = parseInt(fieldset.closest('[data-option-position]')?.dataset.optionPosition || fieldsetIndex + 1, 10)
      let hasBadge = false

      fieldset.querySelectorAll('input[type="radio"]').forEach(input => {
        const label = (input.id && fieldset.querySelector(`label[for="${CSS.escape(input.id)}"]`)) || input.closest('label')
        if (!label) return

        const variant = this.product.variants.find(v =>
          (v.options || []).every((optionValue, i) => (i === position - 1 ? optionValue === input.value : optionValue === currentOptions[i]))
        )
        // Round to the nearest whole dollar (e.g. $5.40 → $5, $5.50 → $6).
        const savings = variant ? Math.round(this.savingsFor(variant, preferredPlan) / 100) * 100 : 0
        let badge = label.querySelector(':scope > .skio-variant-badge')

        if (savings > 0) {
          hasBadge = true
          const text = template.replaceAll('[savings]', this.money(savings, true))

          if (!badge) {
            badge = document.createElement('span')
            badge.className = 'skio-variant-badge'
            label.appendChild(badge)
          }
          if (badge.textContent !== text) badge.textContent = text
          if (!label.classList.contains('skio-has-badge')) label.classList.add('skio-has-badge')
        } else if (badge) {
          badge.remove()
          label.classList.remove('skio-has-badge')
        }
      })

      if (hasBadge !== fieldset.classList.contains('skio-has-badges')) {
        fieldset.classList.toggle('skio-has-badges', hasBadge)
      }
    })
  }

  // !SECTION: Variant option savings badges

  // SECTION: Form + cart

  bindFormEvents() {
    const formId = this.options?.form_id
    this.form = (formId && document.getElementById(formId)) || this.closest('product-form')?.querySelector('form') || this.findProductForm()

    if (!this.form) {
      this.error('No valid product form found')
      return
    }

    if (!this.form.id) this.form.id = `skio-form-${this.product?.id}`

    const sellingPlanInput = this.querySelector('input[name="selling_plan"]')
    if (sellingPlanInput) sellingPlanInput.setAttribute('form', this.form.id)

    this.variantInput = this.form.querySelector('[name="id"]') || document.querySelector(`[name="id"][form="${this.form.id}"]`)

    if (this.variantInput) {
      // Palo Alto's product-info sets this input and fires `change` after every option change.
      this.variantInput.addEventListener('change', e => {
        const variant = this.product?.variants?.find(v => String(v.id) === String(e.target.value))
        this.variantChanged = true

        if (variant) {
          this.variantUnavailable = false
          this.selectedVariant = variant
        } else {
          this.variantUnavailable = true
        }

        this.errorMessage = ''
        this.scheduleBadgeRender()
      })
    }

    const quantityInput = this.form.querySelector('[name="quantity"]') || document.querySelector(`[name="quantity"][form="${this.form.id}"]`)
    if (quantityInput) {
      const syncQuantity = () => (this.quantity = Math.max(1, parseInt(quantityInput.value || '1', 10)))
      syncQuantity()
      quantityInput.addEventListener('input', syncQuantity)
      quantityInput.addEventListener('change', syncQuantity)
    }

    document.addEventListener('variantChanged', e => {
      const variant = this.product?.variants?.find(v => v.id == e.detail?.variantId)
      if (!variant) return
      this.selectedVariant = variant
      this.variantChanged = true
    })
  }

  findProductForm() {
    return (
      Array.from(document.forms).find(form => form.getAttribute('action')?.includes('/cart/add') && form.querySelector('[name="id"]')) || null
    )
  }

  bindCartEvents() {
    document.addEventListener('CartUpdated', event => {
      this.cart = event.detail
    })
  }

  updateForm() {
    const input = this.querySelector('input[name=selling_plan]')
    if (!input) return
    input.value = this.selectedSellingPlan ? this.selectedSellingPlan.id : ''
    input.dispatchEvent(new Event('change'))
  }

  updateExternalPrice() {
    const selector = this.options?.external_price_selector
    if (!selector) return
    document.querySelectorAll(selector).forEach(el => {
      el.innerHTML = this.selectedSellingPlan ? this.price(this.selectedSellingPlan) : this.money(this.selectedVariant.price)
    })
  }

  updateExternalElements() {
    document.querySelectorAll(`[skio-price][skio-key="${this.key}"]`).forEach(el => {
      el.innerHTML = this.price(this.selectedSellingPlan)
    })
    document.querySelectorAll('[skio-subscription-content]').forEach(el => {
      el.style.display = this.selectedSellingPlan ? 'block' : 'none'
    })
  }

  updateURLParams() {
    const url = new URL(window.location.href)
    if (this.selectedSellingPlan) url.searchParams.set('selling_plan', this.selectedSellingPlan.id)
    else url.searchParams.delete('selling_plan')
    window.history.replaceState(window.history.state, '', url.href)
  }

  async handleATCClick(e) {
    e.preventDefault()
    e.stopPropagation()
    if (this.isAdding || !this.selectedVariant || this.variantUnavailable) return
    if (this.product?.requires_selling_plan && !this.selectedSellingPlan) return

    this.isAdding = true
    this.errorMessage = ''

    try {
      await this.addToCart()
    } catch (err) {
      this.error('Add to cart failed', err)
      this.errorMessage = 'Something went wrong. Please try again.'
    } finally {
      this.isAdding = false
    }
  }

  // Mirrors Palo Alto's product-form submit: same endpoint and headers, then
  // `theme:cart:refresh` so the theme's cart drawer re-renders and opens.
  async addToCart() {
    const formData = this.form ? new FormData(this.form) : new FormData()
    formData.set('id', this.selectedVariant.id)
    formData.set('quantity', String(this.quantity || 1))

    if (this.selectedSellingPlan?.id) formData.set('selling_plan', this.selectedSellingPlan.id)
    else formData.delete('selling_plan')

    const response = await fetch(window.theme?.routes?.cart_add_url || '/cart/add', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
      body: formData,
    })
    const json = await response.json()

    if (json.status) {
      this.errorMessage = json.description && typeof json.description === 'string' ? json.description : json.message || 'Could not add to cart.'
      return
    }

    if (window.publish && window.theme?.PUB_SUB_EVENTS?.cartUpdate) {
      window.publish(window.theme.PUB_SUB_EVENTS.cartUpdate, { source: 'skio-plan-picker' })
    }

    this.dispatchEvent(new CustomEvent('skio::added-to-cart', { bubbles: true, composed: true, detail: { response: json, key: this.key } }))

    const hasCartDrawer = window.theme?.settings?.cartType === 'drawer' && document.querySelector('cart-element[data-cart-drawer]')
    if (hasCartDrawer) {
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }))
    } else {
      window.location = window.theme?.routes?.cart_url || '/cart'
    }
  }

  // !SECTION: Form + cart

  // SECTION: Skio discount rules (unchanged from base)

  async fetchRules() {
    const cached = sessionStorage.getItem('skio-discount-rules')
    if (cached) return JSON.parse(cached)

    const rules = await fetch(`https://api.skio.com/storefront-http/get-rules-by-domain-or-hostname?domain=${window?.Shopify?.shop}`)
      .then(response => response.json())
      .then(response => response.rules.filter(rule => rule.type !== 'surpriseDelight').sort((a, b) => a.minQuantityToDiscount - b.minQuantityToDiscount))

    sessionStorage.setItem('skio-discount-rules', JSON.stringify(rules))
    return rules
  }

  eligibleItemCount(rule) {
    const eligibleQuantity = rule?.productVariantIds.some(gid => gid.includes(this.selectedVariant.id)) ? this.quantity : 0
    if (!this.cart?.items) return eligibleQuantity

    const cartItems = Number(
      this.cart.items.reduce(
        (sum, item) =>
          rule?.productVariantIds.some(gid => gid.includes(item.id) && item.selling_plan_allocation && gid.includes(this.selectedVariant.id)) ? sum + item.quantity : sum,
        0
      )
    )
    return cartItems + eligibleQuantity
  }

  getRule() {
    if (!this.selectedVariant || !Array.isArray(this.rules)) return
    let best = { value: -1, index: -1 }

    this.rules.forEach((rule, index) => {
      if (best.value < rule.minQuantityToDiscount && rule.minQuantityToDiscount <= this.eligibleItemCount(rule)) {
        best = { value: rule.minQuantityToDiscount, index }
      }
    })

    this.rule = best.index > -1 ? this.rules[best.index] : null
  }

  // !SECTION: Skio discount rules

  // SECTION: Utilities

  viable() {
    const errors = []
    if (!this.querySelector('input[name=selling_plan]')) errors.push('Missing selling plan input')
    if (!this.product) errors.push('No product found. Pass a product or productHandle to skio-plan-picker.')
    if (!this.selectedVariant) errors.push('No variant found.')
    if (!this.form) errors.push('No product form found. Set form_id or place the picker inside the product form.')
    if (!this.variantInput) errors.push('No variant input (name="id") found in the product form.')

    if (!errors.length) return true
    this.error('Errors', errors)
    return false
  }

  fetchProduct = handle =>
    fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`)
      .then(response => response.json())
      .then(product => {
        this.product = product
        this.selectedVariant = product.variants[0]
        return product
      })

  log(...args) {
    console.log(`%c[skio plan picker][${this.key}]`, 'color: #8770f2;', ...args)
  }

  error(...args) {
    console.error(`%c[skio plan picker][${this.key}]`, 'color: #ff0000;', ...args)
  }

  // !SECTION: Utilities
}

if (!customElements.get('skio-plan-picker')) {
  customElements.define('skio-plan-picker', SkioPlanPicker)
}

if (window) window.SkioPlanPicker = SkioPlanPicker